import { Button } from "@/components/ui/Button";
import { OnlineBookingMinStayPeriodsEditor } from "@/components/onlineBooking/OnlineBookingMinStayPeriodsEditor";
import { updateTouristPanelAvailability } from "@/lib/touristPanelApi";
import type { OnlineBookingMinStayPeriod } from "@/lib/onlineBooking";
import type { TouristPanelDisplay } from "@/types/touristPanel";
import { useCallback, useEffect, useState } from "react";

type Props = {
  workstationCode: string;
  display: TouristPanelDisplay;
  onSaved: (display: TouristPanelDisplay) => void;
};

export function TouristPanelMinStayEditor({ workstationCode, display, onSaved }: Props) {
  const [enabled, setEnabled] = useState(display.availability.min_stay.enabled);
  const [defaultMinNights, setDefaultMinNights] = useState(
    display.availability.min_stay.default_min_nights,
  );
  const [periods, setPeriods] = useState<OnlineBookingMinStayPeriod[]>(
    display.availability.min_stay.periods,
  );
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    setEnabled(display.availability.min_stay.enabled);
    setDefaultMinNights(display.availability.min_stay.default_min_nights);
    setPeriods(display.availability.min_stay.periods);
    setSuccess(false);
  }, [display.availability.min_stay]);

  const onSave = useCallback(async () => {
    setSaving(true);
    setError(null);
    setSuccess(false);
    try {
      const availability = await updateTouristPanelAvailability(workstationCode, {
        min_stay_enabled: enabled,
        min_stay_default_min_nights: Math.max(1, defaultMinNights || 1),
        min_stay_periods: periods,
      });
      onSaved({ ...display, availability });
      setSuccess(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Speichern fehlgeschlagen.");
    } finally {
      setSaving(false);
    }
  }, [defaultMinNights, display, enabled, onSaved, periods, workstationCode]);

  return (
    <section className="mx-auto mb-8 max-w-2xl rounded-xl border border-separator bg-surface p-5 shadow-sm">
      <h2 className="text-lg font-semibold text-label">Mindestaufenthalt</h2>
      <p className="mt-1 text-sm text-label-secondary">
        Legt fest, wie viele Nächte Gäste online mindestens buchen müssen.
      </p>

      <div className="mt-4 space-y-4">
        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={enabled}
            onChange={(e) => {
              setEnabled(e.target.checked);
              setSuccess(false);
            }}
          />
          Mindestaufenthalt für Online-Buchung aktivieren
        </label>

        {enabled ? (
          <>
            <label className="flex max-w-xs flex-col gap-1 text-sm">
              Standard Mindestnächte
              <input
                type="number"
                className="eh-input w-28"
                min={1}
                max={365}
                value={defaultMinNights}
                onChange={(e) => {
                  setDefaultMinNights(Math.max(1, Number(e.target.value) || 1));
                  setSuccess(false);
                }}
              />
              <span className="text-xs text-label-secondary">
                Gilt außerhalb definierter Zeiträume und wenn keine Zeiträume angelegt sind.
              </span>
            </label>

            <OnlineBookingMinStayPeriodsEditor
              periods={periods}
              onChange={(nextPeriods) => {
                setPeriods(nextPeriods);
                setSuccess(false);
              }}
              idPrefix="tourist-panel-min-stay"
            />
          </>
        ) : null}
      </div>

      {error ? (
        <p className="mt-4 text-sm text-red-600" role="alert">
          {error}
        </p>
      ) : null}
      {success ? <p className="mt-4 text-sm text-green-700">Gespeichert.</p> : null}

      <div className="mt-5">
        <Button type="button" onClick={() => void onSave()} disabled={saving}>
          {saving ? "Speichern…" : "Speichern"}
        </Button>
      </div>
    </section>
  );
}
